// Floating Filter FAB + bottom-sheet off-canvas voor Woo archive
// - Sync van state in off-canvas (checkboxes -> hidden inputs)
// - Geen autosubmit in off-canvas; toepassen via "Toon resultaten"
// - Product-count via AJAX voor "Toon resultaten"
// - FAB-badge toont product-count van huidige pagina (DOM)

export default function archiveFilters() {
  const SEL = {
    container:     '.wc-archive',
    open:          '[data-archive-open-filters]',
    close:         '[data-archive-close-filters]',
    panel:         '[data-archive-offcanvas]',
    overlay:       '[data-archive-overlay]',
    badge:         '[data-archive-filter-count]',
    apply:         '[data-archive-apply-filters]',
    actions:       '[data-archive-actions]',
    attrForm:      '.wk-attr-form',
    attrChk:       '.wk-attr__chk',
    priceForm:     '.wk-filter-form',
    priceInput:    '.wk-attr__label--price input',
    metaFound:     '.wk-archive-meta strong',
  };
  const CLS = { open:'is-open', overlay:'is-active', lock:'wk-scroll-lock' };

  const $c = document.querySelector(SEL.container) || document;
  const $panel   = document.querySelector(SEL.panel);
  const $overlay = document.querySelector(SEL.overlay);
  const $badge   = document.querySelector(SEL.badge);
  if (!$panel || !$overlay) return;

  // ===== Helpers =====
  const parseJSON = (s) => { try { return JSON.parse(s); } catch { return {}; } };
  const baseVars = parseJSON($c?.getAttribute('data-query-vars') || '{}');   // bv. product_cat, search, etc.
  const nonce    = $c?.getAttribute('data-nonce') || '';

  let lastFocused = null;
  let unbindFocusTrap = null;

  const setAria = (open) => {
    $panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    $panel.setAttribute('role', 'dialog');
    $panel.setAttribute('aria-label', 'Filters');
    document.querySelectorAll(SEL.open).forEach(btn =>
      btn.setAttribute('aria-expanded', open ? 'true' : 'false')
    );
  };

  const lockScroll = (on) => {
    const b = document.body;
    if (on) {
      const pr = window.innerWidth - document.documentElement.clientWidth;
      if (pr > 0) b.style.paddingRight = `${pr}px`;
      b.classList.add(CLS.lock);
      b.style.overflow = 'hidden';
    } else {
      b.classList.remove(CLS.lock);
      b.style.overflow = '';
      b.style.paddingRight = '';
    }
  };

  const focusables = () => {
    const sel = [
      'a[href]','area[href]','button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])','select:not([disabled])',
      'textarea:not([disabled])','details summary','[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.from($panel.querySelectorAll(sel))
      .filter(el => el.offsetParent !== null || el === document.activeElement);
  };

  const trapFocus = () => {
    const nodes = focusables();
    if (!nodes.length) return () => {};
    const first = nodes[0], last = nodes[nodes.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handler);
    first.focus();
    return () => document.removeEventListener('keydown', handler);
  };

  const open = () => {
    if ($panel.classList.contains(CLS.open)) return;
    lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    $panel.classList.add(CLS.open);
    $overlay.classList.add(CLS.overlay);
    setAria(true);
    lockScroll(true);
    unbindFocusTrap = trapFocus();
    // bij openen: initial count voor "Toon resultaten"
    updateApplyCount();
  };

  const close = () => {
    if (!$panel.classList.contains(CLS.open)) return;
    $panel.classList.remove(CLS.open);
    $overlay.classList.remove(CLS.overlay);
    setAria(false);
    lockScroll(false);
    if (typeof unbindFocusTrap === 'function') unbindFocusTrap();
    unbindFocusTrap = null;
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  };

  // ===== State sync in off-canvas =====
  // Elke attribute widget heeft lijst met checkboxes + een hidden in .wk-attr-form (filter_{attr})
  function syncAttrHiddenFromCheckbox(chk) {
    const widget = chk.closest('.widget');
    const form   = widget?.querySelector(SEL.attrForm);
    if (!form) return;
    const hidden = form.querySelector('input[name^="filter_"]');
    if (!hidden) return;

    const slug = String(chk.value || '').trim();
    const curr = hidden.value ? hidden.value.split(',').map(s=>s.trim()).filter(Boolean) : [];
    const idx  = curr.indexOf(slug);
    if (chk.checked && idx === -1) curr.push(slug);
    if (!chk.checked && idx > -1)  curr.splice(idx, 1);
    hidden.value = curr.join(',');
  }

  function getSelectedPriceRanges() {
    return Array.from($panel.querySelectorAll(`${SEL.priceForm} ${SEL.priceInput}:checked`))
      .map(i => String(i.value || '').trim())
      .filter(Boolean);
  }

  // Prevent submit in off-canvas (we bepalen zelf het moment)
  $panel.addEventListener('submit', (e) => e.preventDefault());

  // Wijzigingen in off-canvas bijhouden
  $panel.addEventListener('change', (e) => {
    const chk = e.target.closest(SEL.attrChk);
    if (chk) {
      syncAttrHiddenFromCheckbox(chk);
      debounceApplyCount();
      return;
    }
    // prijs-range (radio/checkbox)
    if (e.target.closest(SEL.priceForm)) {
      debounceApplyCount();
    }
  });

  // ===== Params bouwen voor apply + count =====
  function buildParamsFromState() {
    const params = new URLSearchParams();

    // Base query vars (categorie, zoek, etc.) uit data-attribute
    Object.entries(baseVars || {}).forEach(([k,v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) v = v.join(',');
      const sv = String(v).trim();
      if (sv !== '') params.set(k, sv);
    });

    // Hidden filter_* en query_type_* uit alle attr-forms
    $panel.querySelectorAll(`${SEL.attrForm} input[name^="filter_"]`).forEach(inp => {
      const k = inp.name; const v = String(inp.value || '').trim();
      if (v) params.set(k, v); else params.delete(k);
    });
    $panel.querySelectorAll(`${SEL.attrForm} input[name^="query_type_"]`).forEach(inp => {
      const k = inp.name; const v = String(inp.value || '').trim();
      if (v) params.set(k, v); else params.delete(k);
    });

    // Price: custom ranges
    const ranges = getSelectedPriceRanges();
    if (ranges.length) {
      params.delete('min_price'); params.delete('max_price'); params.delete('price_range');
      ranges.forEach(v => params.append('price_range', v));
    }

    // Clean paging bij nieuwe filters
    params.delete('paged');

    return params;
  }

  // ===== Apply (navigeren) =====
  function applyFilters() {
    const params = buildParamsFromState();
    close();
    const qs = params.toString();
    window.location.search = qs ? `?${qs}` : window.location.pathname;
  }

  // ===== Count (producten) voor "Toon resultaten" =====
  const ajaxURL = (window.wkAjaxURL) || (window.ajaxurl) || '/wp-admin/admin-ajax.php';
  let countAbort = null;

  async function fetchProductCount() {
    // Gebruik admin-ajax; we geven zelfde queryvars + base mee
    const params = buildParamsFromState();
    params.set('action', 'wk_product_count');
    if (nonce) params.set('_wpnonce', nonce);
    params.set('base', JSON.stringify(baseVars));

    // Abort vorige
    if (countAbort) countAbort.abort();
    countAbort = new AbortController();

    const res = await fetch(`${ajaxURL}?${params.toString()}`, {
      method: 'GET',
      credentials: 'same-origin',
      signal: countAbort.signal,
      headers: { 'Accept': 'application/json' },
    });
    const json = await res.json().catch(() => ({}));
    const total = json?.data?.total ?? json?.total ?? 0;
    return Number(total) || 0;
  }

  const $applyBtn = $panel.querySelector(SEL.apply);
  function setApplyLabel(n, loading=false) {
    if (!$applyBtn) return;
    $applyBtn.disabled = !!loading;
    $applyBtn.textContent = loading
      ? 'Telllen…'
      : (n > 0 ? `Toon resultaten (${n})` : 'Toon resultaten (0)');
  }

  async function updateApplyCount() {
    try {
      setApplyLabel(0, true);
      const n = await fetchProductCount();
      setApplyLabel(n, false);
    } catch {
      setApplyLabel(0, false);
    }
  }
  const debounce = (fn, ms=250) => {
    let t; return (...args)=>{ clearTimeout(t); t=setTimeout(()=>fn(...args), ms); };
  };
  const debounceApplyCount = debounce(updateApplyCount, 250);

  // ===== FAB-badge → productcount uit DOM (huidige pagina) =====
  function updateFabBadgeFromDOM() {
    if (!$badge) return;
    const txt = document.querySelector(SEL.metaFound)?.textContent || '';
    const n = Number(String(txt).replace(/[^\d]/g,'') || 0);
    $badge.textContent = n;
  }

  // ===== Bindings =====
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest(SEL.open))  { e.preventDefault(); open(); }
    if (t.closest(SEL.close)) { e.preventDefault(); close(); }
    if (t.closest(SEL.apply)) { e.preventDefault(); applyFilters(); }
  }, { passive:false });

  $overlay.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

  // Init
  setAria(false);
  updateFabBadgeFromDOM();
  // her-evalueren na navigatie/fragmenten
  ['popstate','wc_fragments_refreshed'].forEach(evt => window.addEventListener(evt, updateFabBadgeFromDOM, { passive:true }));
}
